import React from 'react'
import AccountIcon from './account.svg'
import ArrowIcon from './arrow.svg'
import CartIcon from './cart.svg'
import ExploreIcon from './explore.svg'
import InboxIcon from './inbox.svg'
import Logo from '../Logo'
import ReleaseConsumerIdentityLink from '../ReleaseConsumerIdentityLink'
import { IdentityConsumer } from '#src/containers/Identity'
import { CartConsumer } from '#src/containers/Cart'
import { ChatConsumer } from '@deal/chat-firebase'
import { Box, Grid, GridItem } from '@deal/components'
import { getFilteredLineItems } from '#src/services/cart'
import { generateCreateUrlFromCategory, getOnboardingPathSlug } from '#src/services/path'
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom'
import { NavActiveDepartmentFragment } from '#src/generated/graphql'
import classnames from 'classnames'
import styles from './styles.css'

interface PrimaryNavProps {
  department?: NavActiveDepartmentFragment
  measureRef: (ref: Element | null) => void
  onExploreClicked: React.MouseEventHandler
  transparent?: boolean
}

/**
 * The primary nav is the top bar in the header.
 *
 * Its content is global, or related to the current user:
 *   - Managing the current user's account
 *   - Viewing the current user's conversations (the inbox)
 *   - Viewing the current user's cart
 *   - Viewing the current active department
 *   - Changing the current active department
 */

const PrimaryNav: React.SFC<PrimaryNavProps & RouteComponentProps> = ({
  measureRef,
  onExploreClicked,
  location,
  transparent,
  department
}) => {
  return (
    <IdentityConsumer>
      {({ myself }) => {
        const isImpersonating = myself && myself.user.id !== myself.realUser.id

        return (
          <Grid className={transparent ? '' : styles.container}>
            <GridItem col={2}>
              <nav
                ref={measureRef}
                className={classnames(
                  styles.primaryNav,
                  transparent ? styles.primaryNavTransparent : ''
                )}
              >
                <Box spacing="none" className={styles.box}>
                  <Box.Item className={styles.logoContainer}>
                    <Logo transparent={transparent} />
                  </Box.Item>
                  <Box.Item grow={1}>
                    {myself ? (
                      // Logged In
                      <ul className={styles.navList}>
                        <li className={styles.navItem}>
                          <NavLink
                            to="/inbox"
                            className={styles.navItemLink}
                            activeClassName={styles.navItemLinkActive}
                          >
                            <ChatConsumer>
                              {({ unreadMessageCount }) => {
                                return (
                                  <>
                                    {unreadMessageCount !== undefined &&
                                      unreadMessageCount > 0 && (
                                        <div className={styles.badge}>{unreadMessageCount}</div>
                                      )}
                                    <InboxIcon className={styles.navItemIcon} />
                                    <span className={styles.navItemLabel}>Messages</span>
                                  </>
                                )
                              }}
                            </ChatConsumer>
                          </NavLink>
                        </li>
                        <li className={styles.navItem}>
                          <NavLink
                            to={{
                              ...location,
                              hash: '#cart'
                            }}
                            className={styles.navItemLink}
                            activeClassName={styles.navItemLinkActive}
                            isActive={(_match, location) => location.hash === '#cart'}
                            data-testid="cart-link"
                          >
                            <CartConsumer>
                              {({ order }) => {
                                const lineItemCount = order
                                  ? getFilteredLineItems(order.lineItems).length
                                  : 0

                                return (
                                  <>
                                    {lineItemCount > 0 && (
                                      <div className={styles.badge}>{lineItemCount}</div>
                                    )}
                                    <CartIcon className={styles.navItemIcon} />
                                    <span className={styles.navItemLabel}>Cart</span>
                                  </>
                                )
                              }}
                            </CartConsumer>
                          </NavLink>
                        </li>
                        <li className={styles.navItemUserMenu}>
                          <NavLink
                            to="/account"
                            className={styles.navItemLink}
                            activeClassName={styles.navItemLinkActive}
                          >
                            <AccountIcon className={styles.navItemIcon} />
                            <span className={styles.navItemLabel}>
                              {myself.user.firstName}
                              {isImpersonating && ` (${myself.realUser.firstName})`}
                            </span>
                            <ArrowIcon className={styles.arrowIcon} />
                          </NavLink>

                          {/* Note: This menu is desktop-only. If we change these links, make sure to also change the mobile menu. */}
                          <ul className={styles.userMenuList}>
                            <li className={styles.userMenuListItem}>
                              <NavLink
                                to="/account"
                                className={styles.userMenuListItemLink}
                                activeClassName={styles.userMenuListItemLinkActive}
                              >
                                <span>My account</span>
                              </NavLink>
                            </li>
                            <li className={styles.userMenuListItem}>
                              <NavLink
                                to="/account/orders"
                                className={styles.userMenuListItemLink}
                                activeClassName={styles.userMenuListItemLinkActive}
                              >
                                <span> My orders</span>
                              </NavLink>
                            </li>
                            <li className={styles.userMenuListItem}>
                              <NavLink
                                to="/favorites"
                                className={styles.userMenuListItemLink}
                                activeClassName={styles.userMenuListItemLinkActive}
                              >
                                <span> My favorites</span>
                              </NavLink>
                            </li>
                            <li className={styles.userMenuListItem}>
                              <NavLink
                                to="/support"
                                className={styles.userMenuListItemLink}
                                activeClassName={styles.userMenuListItemLinkActive}
                              >
                                <span>Customer support</span>
                              </NavLink>
                            </li>
                            {isImpersonating && (
                              <li className={styles.userMenuListItem}>
                                <ReleaseConsumerIdentityLink
                                  className={styles.userMenuListItemLink}
                                >
                                  <span>Back to "{myself.realUser.firstName}"</span>
                                </ReleaseConsumerIdentityLink>
                              </li>
                            )}
                            <li className={styles.userMenuListItem}>
                              <NavLink
                                to="/auth/logout"
                                className={styles.userMenuListItemLink}
                                activeClassName={styles.userMenuListItemLinkActive}
                              >
                                <span>Sign out</span>
                              </NavLink>
                            </li>
                          </ul>
                        </li>
                        <li className={styles.navItem}>
                          <a onClick={onExploreClicked} className={styles.navItemLink}>
                            <ExploreIcon className={styles.navItemIcon} />
                            <span className={styles.navItemLabel}>Explore</span>
                          </a>
                        </li>
                      </ul>
                    ) : (
                      // Logged Out
                      <ul className={styles.loggedOutNavList}>
                        <li className={styles.navItem}>
                          <NavLink
                            to={`/auth/login?then=${location.pathname}${location.search}`}
                            className={styles.signIn}
                          >
                            Sign in
                          </NavLink>
                        </li>
                        <li className={styles.desktopNavItem}>
                          <NavLink
                            to={
                              department && department.topLevelCategories[0]
                                ? generateCreateUrlFromCategory(
                                    department.topLevelCategories[0].slug
                                  )
                                : getOnboardingPathSlug()
                            }
                            className={styles.getStarted}
                          >
                            Get started
                          </NavLink>
                        </li>
                      </ul>
                    )}
                  </Box.Item>
                </Box>
              </nav>
            </GridItem>
          </Grid>
        )
      }}
    </IdentityConsumer>
  )
}

export default React.memo(withRouter(PrimaryNav))
